<template>
  <v-app>
    <!-- ===================== MODAL ===================== -->
    <v-dialog v-model="localDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h6 bg-blue-grey-lighten-4">
          <span>{{ isEntryMode ? "Add Production POS" : "Update Production POS" }}</span>
        </v-card-title>

        <v-card-text>
          <v-form @submit.prevent="submit" ref="formRef">
            <v-row>

              <!-- Style Select -->
              <v-col lg="12" md="12" cols="12">
                <v-select
                  v-model="style_id.value.value"
                  :items="styleList"
                  :item-title="item => `${item.code}`"
                  :item-value="item => item.id"
                  label="Select Style"
                  variant="outlined"
                  density="compact"
                  :error-messages="style_id.errorMessage.value"
                  clearable
                >
                  <template #label>
                    Style <span class="text-red-accent-4">*</span>
                  </template>
                </v-select>
              </v-col>

              <!-- Ship Date -->
              <v-col lg="12" md="12" cols="12">
                <v-date-input
                  label="Ship Date"
                  density="compact"
                  prepend-icon=""
                  prepend-inner-icon="mdi-calendar-month"
                  variant="outlined"
                  bg-color="grey-lighten-4"
                  v-model="ship_date.value.value"
                  :error-messages="ship_date.errorMessage.value"
                  autocomplete="off"
                  clearable
                ></v-date-input>
              </v-col>

              <!-- Status -->
              <v-col lg="12" md="12" cols="12">
                <v-checkbox
                  label="Active"
                  v-model="status.value.value"
                  :error-messages="status.errorMessage.value"
                  density="compact"
                  variant="outlined"
                  hide-details
                  single-line
                  color="primary"
                  :true-value="1"
                  :false-value="0"
                ></v-checkbox>
              </v-col>

              <!-- Hidden ID -->
              <input type="hidden" v-model="pos_id.value.value" />

            </v-row>

            <v-divider></v-divider>
            <v-card-actions class="justify-end">
              <v-btn variant="flat" color="red" @click="closeDialog">Close</v-btn>
              <v-btn class="ms-2" variant="flat" color="primary" type="submit">
                {{ isEntryMode ? "Save" : "Update" }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ===================== PAGE CONTENT ===================== -->
    <v-container fluid>
      <v-row>
        <v-col cols="12">

          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title class="bg-blue-grey-lighten-4">
                Production POS List
              </v-expansion-panel-title>

              <v-expansion-panel-text>

                <!-- Search + Add Button -->
                <v-row class="py-2">
                  <v-col md="4">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      variant="outlined"
                      density="compact"
                      hide-details
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="3" offset-md="5" class="text-right">
                    <v-btn
                      color="primary"
                      variant="tonal"
                      prepend-icon="mdi-plus"
                      @click="openCreate"
                      rounded="xl"
                    >
                      Add Production POS
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- DATA TABLE -->
                <v-data-table
                  :headers="headers"
                  :items="productionPos"
                  :search="search"
                  :loading="loading"
                  density="compact"
                >
                  <template v-slot:item.index="{ index }">
                    {{ index + 1 }}
                  </template>

                  <template v-slot:item.style="{ item }">
                    {{ item.style?.code }} - {{ item.style?.name }}
                  </template>

                  <template v-slot:item.ship_date="{ item }">
                    {{ item.ship_date }}
                  </template>

                  <template v-slot:item.status="{ item }">
                    <v-chip size="small" :color="item.status == 1 ? 'success' : 'red'">
                      {{ item.status == 1 ? "Active" : "Inactive" }}
                    </v-chip>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-icon color="primary" @click="openEdit(item)">mdi-pencil-outline</v-icon>
                  </template>
                </v-data-table>

              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

        </v-col>
      </v-row>
    </v-container>

  </v-app>
</template>

<script>
import { ref } from "vue";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";

export default {
  data() {
    return {
      panel: [0],
      dialog: false,
      localDialog: false,
      loading: false,
      search: "",
      isEntryMode: true,
      headers: [
        { title: "#SL", key: "index", align: "center" },
        { title: "Code", key: "code" },
        { title: "Style", key: "style" },
        { title: "Ship Date", key: "ship_date" },
        { title: "Status", key: "status", align: "center" },
        { title: "Actions", key: "actions", align: "center", sortable: false },
      ],
    };
  },

  setup() {
    const store = useStore();
    const localDialog = ref(false);

    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        style_id: yup.number().required().label("Style"),
        ship_date: yup.mixed().required().label("Ship Date"),
        status: yup.boolean().required().label("Status"),
      }),
    });

    const style_id = useField("style_id");
    const ship_date = useField("ship_date");
    const status = useField("status");
    const pos_id = useField("pos_id");

    const closeDialog = () => {
      localDialog.value = false;
      resetForm();
    };

    const submit = handleSubmit((values) => {
      // Format date to YYYY-MM-DD format
      let shipDate = null;
      if (values.ship_date) {
        if (values.ship_date instanceof Date) {
          shipDate = values.ship_date.toISOString().split('T')[0];
        } else if (typeof values.ship_date === 'string') {
          // If it's already a string, try to format it
          const date = new Date(values.ship_date);
          if (!isNaN(date.getTime())) {
            shipDate = date.toISOString().split('T')[0];
          } else {
            shipDate = values.ship_date; // Use as-is if already formatted
          }
        } else {
          shipDate = values.ship_date;
        }
      }

      const payload = {
        style_id: values.style_id,
        ship_date: formattedDate(shipDate),
        status: values.status ? 1 : 0, // boolean → number
      };

      const id = values.pos_id;

      if (id) {
        store
          .dispatch("updateProductionPos", { id, data: payload })
          .then(() => {
            toast.success("Updated Successfully!");
            resetForm();
            closeDialog();
            store.dispatch("getProductionPosList");
          })
          .catch((err) => {
            toast.error(err.response?.data?.message || "Something went wrong");
          });
      } else {
        store
          .dispatch("saveProductionPos", payload)
          .then(() => {
            toast.success("Created Successfully!");
            resetForm();
            closeDialog();
            store.dispatch("getProductionPosList");
          })
          .catch((err) => {
            toast.error(err.response?.data?.message || "Something went wrong");
          });
      }
    });

    return {
      style_id,
      ship_date,
      status,
      pos_id,
      submit,
      resetForm,
      localDialog,
      closeDialog,
    };
  },

  computed: {
    productionPos() {
      return this.$store.getters.getProductionPosList;
    },
    styleList() {
      return this.$store.getters.getProductionStyleList;
    },
  },

  methods: {
    loadList() {
      this.loading = true;
      this.$store.dispatch("getProductionPosList").finally(() => {
        this.loading = false;
      });
      this.$store.dispatch("getProductionStyleList");
    },

    openCreate() {
      this.isEntryMode = true;
      this.localDialog = true;
      this.resetForm();
      this.status.setValue(1); // default checked
    },

    openEdit(item) {
      this.isEntryMode = false;
      this.localDialog = true;

      this.style_id.setValue(item.style_id || "");
      this.ship_date.setValue(item.ship_date || "");
      this.status.setValue(item.status == 1 ? 1 : 0); // Set to 1 or 0, not boolean
      this.pos_id.setValue(item.id);
    },
  },

  watch: {
    dialog(val) {
      this.localDialog = val;
    },
    localDialog(val) {
      this.dialog = val;
    },
  },

  created() {
    this.loadList();
  },
};
</script>
